import styled from '@emotion/styled';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import React from 'react';

import CloseIcon from 'assets/icons/basil_cross-outline.svg?react';
import SearchIconOpened from 'assets/icons/famicons_search-outline.svg?react';
import SearchIcon from 'assets/icons/search_icon.svg?react';

const IconButtonStyled = styled(IconButton)(({ ml }) => ({
  padding: 0,
  cursor: 'pointer',
  marginLeft: ml || 0,
  '& svg': {
    width: 40,
    height: 40
  }
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  cursor: 'pointer',
  '& svg': {
    width: 24,
    height: 24
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  }
}));

const TextFieldStyled = styled(TextField)(({ theme, fullWidth }) => ({
  border: '1px solid rgba(10, 10, 10, 1)',
  width: fullWidth ? '100%' : 550,
  '& .MuiOutlinedInput-input': {
    fontSize: 15,
    padding: '7px 14px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

const CloseIconStyled = styled(CloseIcon)(() => ({ cursor: 'pointer' }));

const SearchFieldWrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'fullWidth' })(
  ({ theme, open, fullWidth }) => ({
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    transition: 'width 220ms ease, opacity 220ms ease',
    width: open ? (fullWidth ? '100%' : 550) : 0,
    opacity: open ? 1 : 0,
    marginLeft: open ? 8 : 0,
    [theme.breakpoints.down('sm')]: {
      width: open ? '100%' : 0,
      marginLeft: 0
    }
  })
);

const Wrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'fullWidth' })(
  ({ theme, isOpen, fullWidth }) => ({
    display: 'flex',
    alignItems: 'center',
    width: fullWidth && isOpen ? '100%' : 'auto',
    [theme.breakpoints.down('sm')]: {
      width: isOpen ? '100%' : 'auto'
    }
  })
);

const SearchToggleInput = ({
  open,
  defaultOpen = false,
  onToggleOpen,
  onClose,
  onOpenChange,
  query,
  onChange,
  onKeyDown,
  onSearch,
  placeholder,
  openAriaLabel,
  closeAriaLabel,
  searchAriaLabel,
  fullWidth = false
}) => {
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = React.useState(!!defaultOpen);
  const isOpen = isControlled ? open : internalOpen;
  const inputRef = React.useRef(null);
  const getValue = React.useCallback(() => inputRef.current?.value ?? query ?? '', [query]);

  const notifyChange = (nextOpen) => {
    if (typeof onOpenChange === 'function') onOpenChange(nextOpen);
  };

  const handleOpenClick = () => {
    if (isControlled) {
      if (typeof onToggleOpen === 'function') onToggleOpen();
      notifyChange(true);
    } else {
      setInternalOpen(true);
      notifyChange(true);
    }
  };

  const handleCloseClick = () => {
    if (isControlled) {
      if (typeof onClose === 'function') onClose();
      notifyChange(false);
    } else {
      setInternalOpen(false);
      notifyChange(false);
    }
  };

  const handleKeyDownCombined = (e) => {
    if (e.key === 'Enter' && typeof onSearch === 'function') {
      e.preventDefault();
      onSearch(getValue());
      return;
    }
    if (typeof onKeyDown === 'function') onKeyDown(e);
    if (!e.defaultPrevented && e.key === 'Escape' && !isControlled) {
      setInternalOpen(false);
      notifyChange(false);
    }
  };

  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      const id = setTimeout(() => {
        try {
          inputRef.current.focus();
        } catch (_) {}
      }, 0);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  const handleClickSearch = () => {
    if (typeof onSearch === 'function') onSearch(getValue());
  };

  return (
    <Wrapper isOpen={isOpen} fullWidth={fullWidth}>
      {!isOpen && (
        <SearchIconButton aria-label={openAriaLabel} onClick={handleOpenClick}>
          <SearchIcon />
        </SearchIconButton>
      )}

      <SearchFieldWrapper open={isOpen} fullWidth={fullWidth}>
        <IconButtonStyled aria-label={closeAriaLabel} onClick={handleCloseClick}>
          <CloseIconStyled />
        </IconButtonStyled>

        <TextFieldStyled
          value={query}
          onChange={onChange}
          onKeyDown={handleKeyDownCombined}
          autoFocus={isOpen}
          inputRef={inputRef}
          variant="outlined"
          placeholder={placeholder}
          fullWidth={fullWidth}
        />

        <IconButtonStyled aria-label={searchAriaLabel} onClick={handleClickSearch}>
          <SearchIconOpened />
        </IconButtonStyled>
      </SearchFieldWrapper>
    </Wrapper>
  );
};

export default SearchToggleInput;
